import { createAsyncThunk } from '@reduxjs/toolkit';
import { AssetType, IAsset } from 'types/app/asset';
import { AxiosWrapper } from './axios.wrapper';
import { updateAsset } from 'core/slices/assets';
import { LoadingState } from 'types/domain/base';
import { loaders } from 'core/sagas/product/assets/load';
//import { AudioLoader } from 'three/src/loaders/AudioLoader';

import axios, { CancelTokenSource } from 'axios';
import { IUploadFile, uploadActions } from 'core/slices/upload';
import { IKeyArray } from 'types/IKeyArray';
import { debounce } from 'lodash';
import { isIOS, isSafari } from 'react-device-detect';
import { dashjs, dashStreamingSettings } from 'utils/loadDash';
import { errorRequest } from './product';
import { IError } from 'types/app/error';
import { assetLog, assetWarn } from 'utils/assetLog';

export const getAsset = createAsyncThunk('asset/get', async (data: Partial<IAsset>) => {
  const response = await AxiosWrapper.getAsset(data.url);
  return response.data;
});

export const loadAsset = createAsyncThunk(
  'asset/load',
  async (data: IAsset, { dispatch, rejectWithValue, fulfillWithValue }) => {
    assetLog('load:start', data.id, {
      type: data.type,
      url: data.url,
      attempts: data.attempts,
      start: data.start,
    });

    try {
      if (data.type === AssetType.VIDEO) {
        if (isIOS || isSafari) {
          const manifest = await fetch(data.url, { credentials: 'include' });
          if (!manifest.ok) throw new Error(`${manifest.status} loading ${data.url}`);
        } else {
          const dash = await dashjs();
          loaders[data.id] = dash.MediaPlayer().create();
          loaders[data.id].initialize(null, data.url || data.fallback, false, data.start);
          loaders[data.id].updateSettings(dashStreamingSettings);
          // Deliberately not awaited. This player preloads with a null video element and
          // playback attaches its own later, so its events describe the preload only — the
          // `no periods in the MPD` error it raises here is routine and does not mean the
          // video is broken. Awaiting it, or retrying on it, breaks playback.
          loaders[data.id].on(dash.MediaPlayer.events.ERROR, (e: any) =>
            assetLog('dash:preload-error', data.id, e?.error?.message || e?.error || e),
          );
          loaders[data.id].on('manifestLoaded', () => assetLog('dash:manifest', data.id, data.url));
          loaders[data.id].preload();
        }
      } else {
        if (data.type === AssetType.IMAGE) {
          const file = await fetch(data.url, { credentials: 'include' });
          // fetch only rejects on network errors, so a missing derivative (403/404) would
          // otherwise slip through and fail later inside the texture loader.
          if (!file.ok) throw new Error(`${file.status} loading ${data.url}`);
          //@ts-ignore
          const { TextureLoader } = await import('three/src/loaders/TextureLoader');
          loaders[data.id] = new TextureLoader();
        }
        //if (data.type === AssetType.AUDIO) loaders[data.id] = new AudioLoader();
        if (data.type === AssetType.MODEL) {
          //@ts-ignore
          const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader');
          //@ts-ignore
          const { DRACOLoader } = await import('three/examples/jsm/loaders/DRACOLoader');
          loaders[data.id] = new GLTFLoader();
          loaders[data.id].draco = new DRACOLoader();
          loaders[data.id].draco.setDecoderPath('/libs/draco/');
          loaders[data.id].draco.preload();
          loaders[data.id].setDRACOLoader(loaders[data.id].draco);
        }

        // Progress counts bytes downloaded, never parsed output. Reporting LOADED from here
        // publishes the asset before `loaders[id].asset` is assigned — GLTF parse and DRACO
        // decode run after the final progress event — so a model scene mounts around an empty
        // object and measures its centring and camera fit on nothing. The dispatch after the
        // await is the real LOADED.
        const onProgress = debounce(
          event =>
            dispatch(
              updateAsset({
                id: data.id,
                loading: LoadingState.LOADING,
                progress: event.loaded / event.total,
              }),
            ),
          100,
        );

        try {
          loaders[data.id].asset = await loaders[data.id].loadAsync(data.url, onProgress);
        } finally {
          // The trailing edge fires up to 100ms after the load resolves, landing a LOADING
          // dispatch on top of the LOADED one below. Consumers gated on the loaded state
          // then unmount an asset that is in fact ready — the model appears, then vanishes.
          onProgress.cancel();
        }
      }

      assetLog('load:ok', data.id, data.url);
      dispatch(updateAsset({ id: data.id, loading: LoadingState.LOADED, progress: 1 }));
      return fulfillWithValue(true);
    } catch (e: any) {
      assetWarn('load:fail', data.id, { url: data.url, error: e?.message, attempts: data.attempts });
      dispatch(updateAsset({ id: data.id, loading: LoadingState.ERROR }));
      dispatch(errorRequest({ code: 3, message: e.message, stackTrace: 'asset load failed' } as IError));
      // Must be returned — calling it alone leaves the thunk resolving, so `loadAsset.rejected`
      // never fires and nothing downstream can react to the failure.
      return rejectWithValue(e);
    }
  },
);

export const CancelTokens: IKeyArray<CancelTokenSource> = {};

export const uploadRequest = createAsyncThunk('api/admin/upload/file', async (data: IUploadFile, { dispatch }) => {
  const file = await fetch(data.ref).then(r => r.blob());
  CancelTokens[data.id] = axios.CancelToken.source();
  if (!data.sceneConfigId) URL.revokeObjectURL(data.ref);

  //dispatch(uploadActions.uploadStarted({ data, cancelToken: cancelToken.token }));

  const response = await axios.put(data.signedUrl, file, {
    cancelToken: CancelTokens[data.id].token,
    headers: { 'Content-Type': data.contentType },
    onUploadProgress: progressEvent =>
      dispatch(
        uploadActions.uploadProgress({
          id: data.id,
          progress: Math.round((progressEvent.loaded * 100) / progressEvent.total) / 100,
        }),
      ),
  });
  return response.data;
});
